/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Loader } from "../../components";
import { getAniosCierreDisponibles, getAniosFacturaDisponibles } from "../../constants/anios";
import { BROKER_ACTIVACION } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import regionLogo from "../../img/region-logo.png";
import { getContratos, getOportunidades, getUsers, getClientes, getEdificios, getIntermediarios } from "../../utils";
import {
  calcularPublicidadMensual,
  calcularBTLMensual,
  calcularHardwareMensual,
  calcularGestionContenidosMensual,
  normalizeMonth,
  convertirAMonedaLocal,
} from "../../utils/calculateVentas";
import { detalleProyeccionCarteleria } from "../../utils/carteleria/detalleProyeccionCarteleria";
import { getClientesPorBroker } from "../../utils/cotizacionesGuardadas";
import { loadExchangeRates } from "../../utils/exchangeRateService";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { matchGrupoOpp } from "../../utils/grupoBroker";
import { resolverNombreEjecutivo } from "../../utils/resolverNombreEjecutivo";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";

import { ChartModal } from "./components/ChartModal";
import { DetailsModal } from "./components/DetailsModal";
import { PipelineModal } from "./components/PipelineModal";
import { VentasTable } from "./components/VentasTable";
import { VPHeader } from "./components/VPHeader";
import { FACTOR_90, FACTOR_75 } from "./constants";
import { useVPAggregation } from "./hooks/useVPAggregation";
import { useVPCierresCalcs } from "./hooks/useVPCierresCalcs";
import { useVPFacturacionCalcs } from "./hooks/useVPFacturacionCalcs";
import { useVPFormatting } from "./hooks/useVPFormatting";
import { useVPPipelineCalcs } from "./hooks/useVPPipelineCalcs";
import styles from "./components/stylesVentasTabla.module.scss";

import "./stylesVentasProyeccion.scss";

const VentasProyeccion = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  // UI State
  // Año que muestra toda la vista (tabla, totales, gráfico). Default = año en curso.
  const [anoSeleccionado, setAnoSeleccionado] = useState(currentYear);
  const anoAA = anoSeleccionado - 1; // año anterior (comparativo)
  const anoAAA = anoSeleccionado - 2; // año anterior anterior (comparativo facturación)
  const [showInDollars, setShowInDollars] = useState(false);
  const [mostrar2025, setMostrar2025] = useState(false);
  const [vistaCompleta, setVistaCompleta] = useState("cierres");
  const [verChileYPeru, setVerChileYPeru] = useState(false);
  const [verYTD, setVerYTD] = useState(false);
  // Filtro de grupo Todos/Visiona/AdLab (Perú-only). "" = Todos.
  const [filtroGrupo, setFiltroGrupo] = useState("");

  // Data State - país actual
  const [oportunidades, setOportunidades] = useState([]);
  const [salesBTL, setSalesBTL] = useState([]);
  const [negociosCarteleria, setNegociosCarteleria] = useState([]);
  const [costosGestionData, setCostosGestionData] = useState([]);
  const [contratosData, setContratosData] = useState([]);

  // Data State - otro país (modo combinado)
  const [oportunidadesOtroPais, setOportunidadesOtroPais] = useState([]);
  const [salesBTLOtroPais, setSalesBTLOtroPais] = useState([]);
  const [negociosCarteleriaOtroPais, setNegociosCarteleriaOtroPais] = useState([]);
  const [costosGestionDataOtroPais, setCostosGestionDataOtroPais] = useState([]);
  const [contratosDataOtroPais, setContratosDataOtroPais] = useState([]);
  const [datosOtroPaisCargados, setDatosOtroPaisCargados] = useState(false);

  // Modal State
  const [modal, setModal] = useState({ open: false, title: "", items: [] });
  const [pipelineModal, setPipelineModal] = useState({ open: false, title: "", items: [], total: 0 });
  const [pipelineSort, setPipelineSort] = useState("importe");
  const [chartModalOpen, setChartModalOpen] = useState(false);

  // Lookup data
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState({});
  const [edificios, setEdificios] = useState({});
  const [loading, setLoading] = useState(true);

  // Calculated State - país actual. Mapas keyed por año (anoSeleccionado + comparativos).
  const [cierresPorAno, setCierresPorAno] = useState({});
  const [facturacionPorAno, setFacturacionPorAno] = useState({});

  // Calculated State - otro país (modo combinado)
  const [cierresPorAnoOtro, setCierresPorAnoOtro] = useState({});
  const [facturacionPorAnoOtro, setFacturacionPorAnoOtro] = useState({});

  const { pais, rol, agencia } = useAuthContext() || {};
  const modoCombinado = verChileYPeru && datosOtroPaisCargados;

  // Años seleccionables = años con datos (cierre + factura) ∪ año en curso (siempre).
  const aniosDisponibles = useMemo(() => {
    const cierre = getAniosCierreDisponibles([...oportunidades, ...salesBTL], contratosData);
    const factura = getAniosFacturaDisponibles([...oportunidades, ...salesBTL, ...contratosData]);
    const set = new Set([...cierre, ...factura, String(currentYear)]);
    return [...set].map(Number).sort((a, b) => b - a);
  }, [oportunidades, salesBTL, contratosData, currentYear]);

  // === HOOKS ===

  const { dolarMensual, dolarMensualOtroPais, tipoCambioActual, dolarFallback } = useExchangeRates(pais);
  const otroPaisNombre = pais === "Chile" ? "Perú" : "Chile";
  const tipoCambioOtroPais = dolarFallback[otroPaisNombre] || 0;

  const { localString, moneda, getMoneda, getDisplayValue, getMonthName, calcularVariacion, shouldShowVsAA } =
    useVPFormatting(pais, showInDollars, modoCombinado);

  // === FILTRO DE GRUPO (Perú-only) — fuente única para hooks y modales ===
  // Filtra los arrays del PAÍS por matchGrupoOpp antes de alimentar los cálculos. Chile
  // (opp.pais !== "Perú") NUNCA se filtra (modo combinado intacto). filtroGrupo="" → pass-through.
  // esBTL=true solo para salesBTL (pauta y contratos van esBTL=false, período-aware).
  const oportunidadesGrupo = useMemo(
    () => oportunidades.filter((o) => o.pais !== "Perú" || matchGrupoOpp(filtroGrupo, o, clientes[o.client_id], false)),
    [oportunidades, clientes, filtroGrupo]
  );
  const salesBTLGrupo = useMemo(
    () => salesBTL.filter((b) => b.pais !== "Perú" || matchGrupoOpp(filtroGrupo, b, clientes[b.client_id], true)),
    [salesBTL, clientes, filtroGrupo]
  );
  const contratosGrupo = useMemo(
    () => contratosData.filter((c) => c.pais !== "Perú" || matchGrupoOpp(filtroGrupo, c, clientes[c.client_id], false)),
    [contratosData, clientes, filtroGrupo]
  );
  // Cartelería/HW es país-level (sin client_id, no es negocio del broker): bajo AdLab se excluye
  // entera → Todos = Visiona + AdLab cierra y AdLab no cuela cartelería de la casa. Todos/Visiona: as-is.
  const negociosCarteleriaGrupo = useMemo(
    () => (filtroGrupo === "grupo:adlab" ? [] : negociosCarteleria),
    [filtroGrupo, negociosCarteleria]
  );

  const {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  } = useVPCierresCalcs({
    oportunidades: oportunidadesGrupo,
    salesBTL: salesBTLGrupo,
    negociosCarteleria: negociosCarteleriaGrupo,
    contratosData: contratosGrupo,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const {
    calcularFacturacionPubProyeccion,
    calcularFacturacionBTLProyeccion,
    calcularFacturacionContratosProyeccion,
    calcularFacturacionPubProyeccionOtro,
    calcularFacturacionBTLProyeccionOtro,
    calcularFacturacionContratosProyeccionOtro,
  } = useVPFacturacionCalcs({
    oportunidades: oportunidadesGrupo,
    salesBTL: salesBTLGrupo,
    contratosData: contratosGrupo,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const { getDesglose } = useVPAggregation({
    cierresPorAno,
    facturacionPorAno,
    cierresPorAnoOtro,
    facturacionPorAnoOtro,
    showInDollars,
    modoCombinado,
    dolarMensual,
    dolarMensualOtroPais,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
  });

  const {
    calcularPipeline,
    calcularPipelineBruto,
    calcularPipelineBrutoTotal,
    calcularPipelineFacturacion,
    getPipelineItems,
  } = useVPPipelineCalcs({
    oportunidades: oportunidadesGrupo,
    oportunidadesOtroPais,
    contratosData: contratosGrupo,
    contratosOtroPais: contratosDataOtroPais,
    showInDollars,
    modoCombinado,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
    anoSeleccionado,
  });

  // === DATA LOADING ===

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Garantiza que tipos de cambio (UF/USD) esten cargados antes de cualquier
        // calculo que convierta monedas. Evita race condition donde los calculos
        // corren con cache vacio y convierten USD→CLP como 0.
        await loadExchangeRates();
        const proyResults = await Promise.all([
          getOportunidades({ pais }),
          getUsers(),
          client.from("negocios_carteleria").select("*").eq("pais", pais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", pais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", pais),
          getClientes({ pais }),
          getEdificios({ pais }),
          getContratos({ pais }),
          getIntermediarios({ pais }),
        ]);
        const usuariosData = proyResults[1];
        const intermediariosData = proyResults[8] || [];
        const negociosData = proyResults[2];
        const costosGestionRaw = proyResults[4];
        const edificiosData = proyResults[6];
        // Rol BROKER sin scoping de LECTURA en su país: ve toda la proyección de Perú
        // (como comercial/gerencia). El modo combinado (otro país) SÍ sigue scopeado abajo
        // → el broker no ve Chile. La edición/creación sigue acotada a AdLab en otros guards.
        const allOportunidades = proyResults[0];
        const clientesData = proyResults[5];
        const salesBTLData = proyResults[3];
        const contratosRaw = proyResults[7];

        setUsuarios(usuariosData || []);
        setIntermediarios(intermediariosData);
        setNegociosCarteleria(negociosData.data || []);
        setCostosGestionData(costosGestionRaw.data || []);

        const clientesMap = {};
        (clientesData || []).forEach((c) => {
          clientesMap[c.id] = c;
        });
        setClientes(clientesMap);

        const edificiosMap = {};
        (edificiosData || []).forEach((e) => {
          edificiosMap[e.id] = e;
        });
        setEdificios(edificiosMap);

        const filteredOportunidades = allOportunidades
          .filter((o) => o.pais === pais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidades(filteredOportunidades);

        const filteredBTL = (salesBTLData.data || [])
          .filter((btl) => btl.pais === pais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTL(filteredBTL);

        const filteredContratos = (contratosRaw || []).filter((c) => c.pais === pais && !c.eliminada);
        setContratosData(filteredContratos);
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pais, rol, agencia]);

  // Cargar datos del OTRO país cuando se activa modo combinado
  useEffect(() => {
    if (!verChileYPeru || datosOtroPaisCargados) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const fetchDatosOtroPais = async () => {
      try {
        const otroResults = await Promise.all([
          getOportunidades({ pais: otroPais }),
          client.from("negocios_carteleria").select("*").eq("pais", otroPais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", otroPais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", otroPais),
          getContratos({ pais: otroPais }),
        ]);
        const negociosOtro = otroResults[1];
        const costosGestionOtro = otroResults[3];
        let oportunidadesOtro = otroResults[0];
        let salesBTLOtro = otroResults[2];
        let contratosOtro = otroResults[4];

        // === SCOPING BROKER en modo combinado (otro país) — mismo criterio, sin leak ===
        if (rol === ROLES.BROKER) {
          let carteraOtroIds = new Set();
          try {
            const carteraOtro = await getClientesPorBroker(otroPais, agencia);
            carteraOtroIds = new Set((carteraOtro || []).map((c) => Number(c.id)));
          } catch {
            carteraOtroIds = new Set();
          }
          const enCarteraOtro = (cid) => carteraOtroIds.has(Number(cid));
          oportunidadesOtro = (oportunidadesOtro || []).filter((o) => enCarteraOtro(o.client_id));
          salesBTLOtro = {
            ...salesBTLOtro,
            data: (salesBTLOtro?.data || []).filter((b) => enCarteraOtro(b.client_id)),
          };
          contratosOtro = (contratosOtro || []).filter((c) => enCarteraOtro(c.client_id));

          // Filtro temporal broker (otro país): mismo criterio que el bloque principal.
          const brokerDesdeOtro = (o) => !o.fecha_cierre || o.fecha_cierre >= BROKER_ACTIVACION;
          oportunidadesOtro = oportunidadesOtro.filter(brokerDesdeOtro);
          contratosOtro = contratosOtro.filter(brokerDesdeOtro);
          salesBTLOtro = { ...salesBTLOtro, data: (salesBTLOtro?.data || []).filter(brokerDesdeOtro) };
        }

        const filteredOportunidadesOtro = oportunidadesOtro
          .filter((o) => o.pais === otroPais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidadesOtroPais(filteredOportunidadesOtro);

        setNegociosCarteleriaOtroPais(negociosOtro.data || []);
        setCostosGestionDataOtroPais(costosGestionOtro.data || []);

        const filteredBTLOtro = (salesBTLOtro.data || [])
          .filter((btl) => btl.pais === otroPais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTLOtroPais(filteredBTLOtro);

        const filteredContratosOtro = (contratosOtro || []).filter((c) => c.pais === otroPais && !c.eliminada);
        setContratosDataOtroPais(filteredContratosOtro);

        setDatosOtroPaisCargados(true);
      } catch (error) {
        console.error("Error cargando datos del otro país:", error);
      }
    };

    fetchDatosOtroPais();
  }, [verChileYPeru, datosOtroPaisCargados, pais, rol, agencia]);

  // === CALCULATION ORCHESTRATION ===

  useEffect(() => {
    if (loading) return;

    const calcularTodo = async () => {
      try {
        // Re-asegura cache de tipos de cambio antes de calcular
        // (defensivo: si fetchData termino pero el cache se invalido).
        await loadExchangeRates();
        // Helper: suma element-wise de dos arrays de 12
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));

        // CIERRES (contratos sumados en publicidad) — año seleccionado + AA
        const buildCierres = (ano) => ({
          publicidad: sumarArrays(calcularCierresPub(ano), calcularCierresContratos(ano)),
          hardware: calcularCierresHW(ano),
          carteleria: calcularCierresCart(ano),
          btl: calcularCierresBTL(ano),
        });
        setCierresPorAno({ [anoSeleccionado]: buildCierres(anoSeleccionado), [anoAA]: buildCierres(anoAA) });

        // FACTURACIÓN comparativo AAA: solo facturafecha real (sin proyección)
        const buildFacturacionReal = async (ano) => {
          const pub = await calcularPublicidadMensual(ano, oportunidadesGrupo, pais);
          const cont = calcularFacturacionContratosProyeccion(ano);
          const btl = await calcularBTLMensual(ano, salesBTLGrupo, pais);
          const hw = await calcularHardwareMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          const cart = await calcularGestionContenidosMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          return { publicidad: sumarArrays(pub, cont), hardware: hw, carteleria: cart, btl };
        };

        // FACTURACIÓN con proyección (facturafecha + mes_factura) — año seleccionado + AA
        const buildFacturacionProy = async (ano) => {
          const pub = sumarArrays(calcularFacturacionPubProyeccion(ano), calcularFacturacionContratosProyeccion(ano));
          const btl = calcularFacturacionBTLProyeccion(ano);
          const hw = await calcularHardwareMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          const cart = await calcularGestionContenidosMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          return { publicidad: pub, hardware: hw, carteleria: cart, btl };
        };

        setFacturacionPorAno({
          [anoAAA]: await buildFacturacionReal(anoAAA),
          [anoAA]: await buildFacturacionProy(anoAA),
          [anoSeleccionado]: await buildFacturacionProy(anoSeleccionado),
        });
      } catch (error) {
        console.error("Error calculando:", error);
      }
    };

    calcularTodo();
  }, [
    loading,
    oportunidadesGrupo,
    salesBTLGrupo,
    negociosCarteleriaGrupo,
    costosGestionData,
    contratosGrupo,
    pais,
    anoSeleccionado,
    anoAA,
    anoAAA,
  ]);

  // CÁLCULOS OTRO PAÍS (modo combinado)
  useEffect(() => {
    if (!modoCombinado || oportunidadesOtroPais.length === 0) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const calcularTodoOtroPais = async () => {
      try {
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));
        const negocios = negociosCarteleriaOtroPais;
        const costos = costosGestionDataOtroPais;

        // CIERRES otro país — año seleccionado + AA
        const buildCierresOtro = (ano) => ({
          publicidad: sumarArrays(calcularCierresPubOtro(ano), calcularCierresContratosOtro(ano)),
          hardware: calcularCierresHWOtro(ano),
          carteleria: calcularCierresCartOtro(ano),
          btl: calcularCierresBTLOtro(ano),
        });
        setCierresPorAnoOtro({
          [anoSeleccionado]: buildCierresOtro(anoSeleccionado),
          [anoAA]: buildCierresOtro(anoAA),
        });

        // FACTURACIÓN comparativo AAA otro país: solo facturafecha real
        const buildFacturacionRealOtro = async (ano) => {
          const pub = await calcularPublicidadMensual(ano, oportunidadesOtroPais, otroPais);
          const cont = calcularFacturacionContratosProyeccionOtro(ano);
          const btl = await calcularBTLMensual(ano, salesBTLOtroPais, otroPais);
          const hw = await calcularHardwareMensual(ano, negocios, otroPais, costos);
          const cart = await calcularGestionContenidosMensual(ano, negocios, otroPais, costos);
          return { publicidad: sumarArrays(pub, cont), hardware: hw, carteleria: cart, btl };
        };

        // FACTURACIÓN con proyección otro país — año seleccionado + AA
        const buildFacturacionProyOtro = async (ano) => {
          const pub = sumarArrays(
            calcularFacturacionPubProyeccionOtro(ano),
            calcularFacturacionContratosProyeccionOtro(ano)
          );
          const btl = calcularFacturacionBTLProyeccionOtro(ano);
          const hw = await calcularHardwareMensual(ano, negocios, otroPais, costos);
          const cart = await calcularGestionContenidosMensual(ano, negocios, otroPais, costos);
          return { publicidad: pub, hardware: hw, carteleria: cart, btl };
        };

        setFacturacionPorAnoOtro({
          [anoAAA]: await buildFacturacionRealOtro(anoAAA),
          [anoAA]: await buildFacturacionProyOtro(anoAA),
          [anoSeleccionado]: await buildFacturacionProyOtro(anoSeleccionado),
        });
      } catch (error) {
        console.error("Error calculando otro país:", error);
      }
    };

    calcularTodoOtroPais();
  }, [
    modoCombinado,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    costosGestionDataOtroPais,
    contratosDataOtroPais,
    pais,
    anoSeleccionado,
    anoAA,
    anoAAA,
  ]);

  // === MODAL HANDLERS ===

  const handleOpenModal = useCallback(
    (monthIndex, tipo, year, vertical) => {
      const ano = Number(year || currentYear);
      const start = monthIndex + 1;
      const end = monthIndex + 1;
      const isCierres = tipo === "cierres";
      const campoMes = isCierres ? "mes_cierre" : "mes_factura";

      let todas = [];

      if (vertical === "publicidad" || vertical === null) {
        todas = [
          ...todas,
          ...oportunidadesGrupo
            .filter((o) => {
              if (o.estatus !== "100%" || o.eliminada) return false;
              // Misma lógica de prioridad que useVPFacturacionCalcs:
              // facturafecha (real) > mes_factura (proyección) para facturación
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && o[campoMes]) {
                const [mesStr, anoStr] = o[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((o) => {
              // Ejecutivo = dueño de la cuenta (cliente.ejecutivo), fallback al de la venta.
              // Si es broker, se muestra el nombre del intermediario (ej. "AdLab").
              const ejecutivoId = clientes[o.client_id]?.ejecutivo ?? o.ejecutivo;
              const ejecutivoNombre =
                resolverNombreEjecutivo(
                  ejecutivoId,
                  usuarios,
                  intermediarios,
                  o.client_id,
                  clientes,
                  o.created_at,
                  o.estatus,
                  o.fecha_cierre,
                  o.excluir_broker
                ) ?? "Sin asignar";
              // Facturación → facturable (descuento de acuerdo, 7.22); Cierres queda en lista (la venta
              // en sí). El total y las filas del DetailsModal leen este importe → quedan coherentes con
              // la celda de Facturación (useVPFacturacionCalcs, también facturable).
              const importeRaw = isCierres ? parseFloat(o.importe) || 0 : getImporteFacturable(o);
              // Resolver mes con misma prioridad
              let mesIdx = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) mesIdx = d.getMonth();
              }
              if (mesIdx < 0 && o[campoMes]) {
                mesIdx = Number(o[campoMes].split("-")[0]) - 1;
              }
              const ultimoDia = new Date(ano, (mesIdx >= 0 ? mesIdx : 0) + 1, 0);
              const fechaConv = ultimoDia.toISOString().split("T")[0];
              const importeLocal = o.moneda_dolar
                ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConv)
                : importeRaw;
              return {
                ...o,
                esBTL: false,
                esCarteleria: false,
                ejecutivo: ejecutivoNombre,
                cliente: o.cliente || "Sin cliente",
                importe: importeLocal,
                importeOriginalMoneda: importeRaw,
              };
            }),
        ];

        // CONTRATOS (sumados en publicidad)
        if (isCierres) {
          todas = [
            ...todas,
            ...contratosGrupo
              .filter((c) => {
                if (c.estatus !== "100%" || c.eliminada || !c.mes_cierre) return false;
                const [mesStr, anoStr] = c.mes_cierre.split("-");
                return Number(anoStr) === ano && Number(mesStr) >= start && Number(mesStr) <= end;
              })
              .map((c) => {
                const clienteObj = clientes[c.client_id];
                const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
                const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
                const ejecutivoNombre =
                  resolverNombreEjecutivo(
                    ejecutivoId,
                    usuarios,
                    intermediarios,
                    c.client_id,
                    clientes,
                    c.created_at,
                    c.estatus,
                    c.fecha_cierre,
                    c.excluir_broker
                  ) ??
                  (c.display_id || "Sin asignar");
                const importeRaw = Number(c.importe_total) || 0;
                const mesIdxC = Number(c.mes_cierre?.split("-")[0]) - 1;
                const ultimoDiaC = new Date(ano, (mesIdxC >= 0 ? mesIdxC : 0) + 1, 0);
                const fechaConvC = ultimoDiaC.toISOString().split("T")[0];
                const importeLocal = c.moneda_dolar
                  ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConvC)
                  : importeRaw;
                return {
                  ...c,
                  esBTL: false,
                  esCarteleria: false,
                  esHardware: false,
                  esContrato: true,
                  cliente: clienteNombre,
                  ejecutivo: ejecutivoNombre,
                  importe: importeLocal,
                  importeOriginalMoneda: importeRaw,
                };
              }),
          ];
        } else {
          // Facturación: explotar JSONB meses
          contratosGrupo
            .filter((c) => !c.eliminada && c.estatus === "100%")
            .forEach((c) => {
              const clienteObj = clientes[c.client_id];
              const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
              const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
              const ejecutivoNombre =
                resolverNombreEjecutivo(
                  ejecutivoId,
                  usuarios,
                  intermediarios,
                  c.client_id,
                  clientes,
                  c.created_at,
                  c.estatus,
                  c.fecha_cierre,
                  c.excluir_broker
                ) ??
                (c.display_id || "Sin asignar");
              (c.meses || []).forEach((mesObj) => {
                let monthIdx = -1;
                let yearVal = -1;
                if (mesObj.facturafecha) {
                  const d = new Date(mesObj.facturafecha);
                  if (!isNaN(d.getTime())) {
                    monthIdx = d.getMonth() + 1;
                    yearVal = d.getFullYear();
                  }
                } else if (mesObj.mes) {
                  const [m, y] = mesObj.mes.split("-");
                  monthIdx = Number(m);
                  yearVal = Number(y);
                }
                if (yearVal === ano && monthIdx >= start && monthIdx <= end) {
                  // Rama facturación (else de isCierres): facturable con descuento a nivel contrato (7.22).
                  const importeRawC = getImporteFacturable({
                    importe: mesObj.importe,
                    descuento_acuerdo_pct: c.descuento_acuerdo_pct,
                  });
                  const ultimoDiaFact = new Date(ano, monthIdx, 0);
                  const fechaConvFact = ultimoDiaFact.toISOString().split("T")[0];
                  const importeLocalC = c.moneda_dolar
                    ? convertirAMonedaLocal(importeRawC, "USD", pais, fechaConvFact)
                    : importeRawC;
                  todas.push({
                    ...c,
                    id: `${c.id}-${mesObj.mes}`,
                    esBTL: false,
                    esCarteleria: false,
                    esHardware: false,
                    esContrato: true,
                    cliente: clienteNombre,
                    ejecutivo: ejecutivoNombre,
                    importe: importeLocalC,
                    importeOriginalMoneda: importeRawC,
                    mes_factura: mesObj.mes,
                  });
                }
              });
            });
        }
      }

      if (vertical === "btl" || vertical === null) {
        todas = [
          ...todas,
          ...salesBTLGrupo
            .filter((b) => {
              if (b.status !== 100 || b.eliminada) return false;
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && b[campoMes]) {
                const [mesStr, anoStr] = b[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((b) => {
              const invRaw = Number(b.total_investment) || 0;
              let mesIdxBTL = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) mesIdxBTL = d.getMonth();
              }
              if (mesIdxBTL < 0 && b[campoMes]) {
                mesIdxBTL = Number(b[campoMes].split("-")[0]) - 1;
              }
              const ultimoDiaBTL = new Date(ano, (mesIdxBTL >= 0 ? mesIdxBTL : 0) + 1, 0);
              const fechaConvBTL = ultimoDiaBTL.toISOString().split("T")[0];
              const invLocal = b.moneda_dolar ? convertirAMonedaLocal(invRaw, "USD", pais, fechaConvBTL) : invRaw;
              return {
                ...b,
                esBTL: true,
                esCarteleria: false,
                esHardware: false,
                cliente: b.cliente || "Sin cliente",
                total_investment: invLocal,
                importeOriginalMoneda: invRaw,
              };
            }),
        ];
      }

      // HARDWARE + CARTELERÍA: parte numérica en util (F3c: histórico congelado < 2026-10 + motor desde 2026-10).
      if (vertical === "hardware" || vertical === "carteleria" || vertical === null) {
        todas = [
          ...todas,
          ...detalleProyeccionCarteleria({
            negocios: negociosCarteleriaGrupo,
            vertical,
            isCierres,
            ano,
            start,
            end,
            pais,
            edificios,
            clientes,
          }),
        ];
      }

      const isFacturacion = tipo === "facturacion";
      setModal({
        open: true,
        title:
          `${isFacturacion ? "Facturación" : "Cierres"}` +
          `${vertical ? ` - ${vertical.charAt(0).toUpperCase() + vertical.slice(1)}` : ""}` +
          ` - ${getMonthName(monthIndex)} ${ano}`,
        items: todas.sort((a, b) => (b.importe || b.total_investment || 0) - (a.importe || a.total_investment || 0)),
      });
    },
    [
      oportunidadesGrupo,
      salesBTLGrupo,
      negociosCarteleriaGrupo,
      contratosGrupo,
      usuarios,
      intermediarios,
      clientes,
      edificios,
      pais,
      currentYear,
      getMonthName,
    ]
  );

  const handleOpenPipelineModal = useCallback(
    (monthIndex, isTotal = false) => {
      const items = getPipelineItems(monthIndex, isTotal);
      const mesNombre = isTotal ? `TOTAL ${anoSeleccionado}` : getMonthName(monthIndex);

      let total = 0;
      items.forEach((op) => {
        const importe = parseFloat(op.importe) || 0;
        const factor = op.estatus === "90%" ? FACTOR_90 : FACTOR_75;
        const importeAjustado = importe * factor;
        if (showInDollars || modoCombinado) {
          const rateOp = op.paisOrigen === pais ? tipoCambioActual : tipoCambioOtroPais || tipoCambioActual;
          total += importeAjustado / rateOp;
        } else {
          total += importeAjustado;
        }
      });

      setPipelineModal({
        open: true,
        title: `Pipeline - ${mesNombre}`,
        items,
        total,
      });
    },
    [
      getPipelineItems,
      getMonthName,
      showInDollars,
      modoCombinado,
      tipoCambioActual,
      tipoCambioOtroPais,
      pais,
      anoSeleccionado,
    ]
  );

  // === RENDER ===

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="ventas-proyeccion-wrapper">
      <VPHeader
        vistaCompleta={vistaCompleta}
        setVistaCompleta={setVistaCompleta}
        mostrar2025={mostrar2025}
        setMostrar2025={setMostrar2025}
        showInDollars={showInDollars}
        setShowInDollars={setShowInDollars}
        verChileYPeru={verChileYPeru}
        setVerChileYPeru={setVerChileYPeru}
        verYTD={verYTD}
        setVerYTD={setVerYTD}
        filtroGrupo={filtroGrupo}
        setFiltroGrupo={setFiltroGrupo}
        pais={pais}
        logo={verChileYPeru ? regionLogo : undefined}
        anoSeleccionado={anoSeleccionado}
        setAnoSeleccionado={setAnoSeleccionado}
        aniosDisponibles={aniosDisponibles}
        currentYear={currentYear}
        getDesglose={getDesglose}
        getMoneda={getMoneda}
        getDisplayValue={getDisplayValue}
        getMonthName={getMonthName}
        currentMonthIndex={currentMonthIndex}
        calcularPipeline={calcularPipeline}
        calcularPipelineFacturacion={calcularPipelineFacturacion}
        onOpenChart={() => setChartModalOpen(true)}
      />

      <div className="ventas-proyeccion">
        <div className={styles.tableWrapper}>
          <VentasTable
            vistaCompleta={vistaCompleta}
            mostrar2025={mostrar2025}
            verYTD={verYTD}
            anoSel={anoSeleccionado}
            anoAA={anoAA}
            anoAAA={anoAAA}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            getMonthName={getMonthName}
            calcularVariacion={calcularVariacion}
            shouldShowVsAA={shouldShowVsAA}
            calcularPipelineBruto={calcularPipelineBruto}
            calcularPipelineBrutoTotal={calcularPipelineBrutoTotal}
            onOpenModal={handleOpenModal}
            onOpenPipelineModal={handleOpenPipelineModal}
            currentMonthIndex={currentMonthIndex}
            styles={styles}
          />

          <DetailsModal
            modal={modal}
            onClose={() => setModal({ open: false, title: "", items: [] })}
            showInDollars={showInDollars}
            getMoneda={getMoneda}
            localString={localString}
            pais={pais}
            tipoCambioActual={tipoCambioActual}
            styles={styles}
          />

          <PipelineModal
            pipelineModal={pipelineModal}
            onClose={() => setPipelineModal({ open: false, title: "", items: [], total: 0 })}
            pipelineSort={pipelineSort}
            setPipelineSort={setPipelineSort}
            showInDollars={showInDollars}
            modoCombinado={modoCombinado}
            clientes={clientes}
            moneda={moneda}
            localString={localString}
            tipoCambioActual={tipoCambioActual}
            pais={pais}
            styles={styles}
          />

          <ChartModal
            open={chartModalOpen}
            onClose={() => setChartModalOpen(false)}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            currentMonthIndex={currentMonthIndex}
            anoSel={anoSeleccionado}
            anoAA={anoAA}
            currentYear={currentYear}
            pais={pais}
            styles={styles}
            oportunidades={oportunidades}
            contratosData={contratosData}
            showInDollars={showInDollars}
          />
        </div>
      </div>
    </div>
  );
};

export default VentasProyeccion;
